import React, { useCallback, useState } from 'react';
import { SECONDARY_API_CLIENT } from 'services/API';
import { When } from 'UI/components/atoms';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';

import { AIProspectingResultsTable } from '../AIProspectingResultsTable';
import { Conversation } from '../Conversation';

import {
  AI_PROSPECTING_ASSISTANT_BASE_PATH,
  COPIES
} from './AIProspectingAssistantDrawer.constants';
import { useDrawerStyles } from './AIProspectingAssistantDrawer.styled';
import { formatPostResponseData } from './AIProspectingAssistantDrawer.utils';
import { MessageMetaData } from './MessageMetaData';

export const AIProspectingAssistantDrawer = ({ initialSearchText, open, onClose }) => {
  const classes = useDrawerStyles();
  const [currentResponseId, setCurrentResponseId] = useState(null);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [shouldAutoSelectResponse, setShouldAutoSelectResponse] = useState(!!initialSearchText);

  const handleClose = (_event, reason) => {
    if (reason === 'backdropClick') return;
    setIsConfirmationDialogOpen(true);
  };

  const handleMessageReplied = useCallback(
    responseData => {
      if (shouldAutoSelectResponse && responseData?.id) {
        setCurrentResponseId(responseData.id);
        setShouldAutoSelectResponse(false);
      }
    },
    [shouldAutoSelectResponse]
  );

  const handleMetaDataClick = useCallback(responseId => {
    setCurrentResponseId(responseId);
  }, []);

  const metaDataComponent = useCallback(
    ({ data }) => (
      <MessageMetaData
        data={data}
        onClick={handleMetaDataClick}
        isActive={currentResponseId !== null && data?.id === currentResponseId}
      />
    ),
    [handleMetaDataClick, currentResponseId]
  );

  const handleConfirmDrawerClose = () => {
    setIsConfirmationDialogOpen(false);
    onClose && onClose();
  };

  const CustomDialogActions = ({ defaultButtonsProps, ActionsMapper }) => {
    const { onClose: closeDialog } = defaultButtonsProps;

    return (
      <ActionsMapper
        buttons={[
          {
            variant: 'outlined',
            text: COPIES.warningCloseDialog.actions.discardAndExit,
            onClick: handleConfirmDrawerClose
          },
          {
            text: COPIES.warningCloseDialog.actions.cancel,
            onClick: closeDialog
          }
        ]}
      />
    );
  };

  return (
    <FPDrawer
      CardHeaderProps={{
        title: COPIES.title
      }}
      FPDrawerProps={{
        open,
        classes: { paper: classes.drawerLarge },
        ModalProps: { disableEscapeKeyDown: true }
      }}
      onClose={handleClose}
      hasPaddingContent={false}
      hideFooter
    >
      <div className={classes.dualContainers}>
        <div className={classes.aiAssistantContainer}>
          <Conversation
            apiInstance={SECONDARY_API_CLIENT}
            basePath={AI_PROSPECTING_ASSISTANT_BASE_PATH}
            canSelectAIModels={false}
            composerPlaceholder={COPIES.composerPlaceholder}
            formatPostResponseData={formatPostResponseData}
            initialSearchText={initialSearchText}
            messageFullWidth
            metaDataComponent={metaDataComponent}
            onMessageReplied={handleMessageReplied}
          />
        </div>

        <div className={classes.rightContainer}>
          <AIProspectingResultsTable key={currentResponseId} responseId={currentResponseId} />
        </div>
      </div>

      <When condition={isConfirmationDialogOpen}>
        <DecisionDialog
          CustomActions={CustomDialogActions}
          isOpened
          message={COPIES.warningCloseDialog.message}
          onClose={() => setIsConfirmationDialogOpen(false)}
          onConfirm={handleConfirmDrawerClose}
          showSeverityIcon
          title={COPIES.warningCloseDialog.title}
        />
      </When>
    </FPDrawer>
  );
};
